import { MEDIA_API_PREFIX } from '../config/site';
import { ImageFormat, ConvertApiResponse } from '../types/media';

/**
 * Backend contract:
 *
 * POST {MEDIA_API_PREFIX}/convert (multipart: file, fromFormat, toFormat)
 *   202: { jobId } — connect to SSE for progress + result
 *   200: { downloadUrl?, fileName?, mimeType? } when X-Prefer-Sync: true
 *
 * GET {MEDIA_API_PREFIX}/jobs/:jobId/events — SSE (progress | complete | error)
 *
 * GET {MEDIA_API_PREFIX}/formats — { formats: string[] }
 * GET {MEDIA_API_PREFIX}/health — { status: "ok" }
 */

export class MediaApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'MediaApiError';
  }
}

interface JobAcceptedResponse {
  jobId: string;
}

interface SseProgressPayload {
  stage: string;
  percent: number;
  message?: string;
}

interface SseCompletePayload {
  downloadUrl: string;
  fileName: string;
  mimeType: string;
}

interface SseErrorPayload {
  code: string;
  message: string;
}

function stageToPercent(stage: string, serverPercent: number, uploadCap: number): number {
  const uploadWeight = uploadCap;
  const serverWeight = 100 - uploadWeight;
  const serverScaled = Math.round((serverPercent / 100) * serverWeight);
  return Math.min(100, uploadWeight + serverScaled);
}

async function parseErrorResponse(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const body = (await response.json()) as {
        message?: string;
        error?: string;
      };
      return body.message || body.error || response.statusText;
    } catch {
      /* fall through */
    }
  }
  try {
    const text = await response.text();
    return text.slice(0, 200) || response.statusText;
  } catch {
    return response.statusText || 'Request failed';
  }
}

function uploadWithProgress(
  url: string,
  formData: FormData,
  onUploadProgress?: (percent: number) => void,
): Promise<{ status: number; body: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.responseType = 'text';

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onUploadProgress) return;
      const pct = Math.round((event.loaded / event.total) * 100);
      onUploadProgress(pct);
    };

    xhr.onload = () => {
      resolve({
        status: xhr.status,
        body: xhr.responseText,
        contentType: xhr.getResponseHeader('content-type') || '',
      });
    };

    xhr.onerror = () => reject(new MediaApiError('Network error during upload'));
    xhr.ontimeout = () => reject(new MediaApiError('Upload timed out'));
    xhr.send(formData);
  });
}

function watchJobEvents(
  jobId: string,
  onProgress?: (percent: number) => void,
  uploadCap = 35,
): Promise<SseCompletePayload> {
  return new Promise((resolve, reject) => {
    const url = `${MEDIA_API_PREFIX}/jobs/${jobId}/events`;
    const es = new EventSource(url);

    const reportServer = (payload: SseProgressPayload) => {
      onProgress?.(stageToPercent(payload.stage, payload.percent, uploadCap));
    };

    es.addEventListener('progress', (event) => {
      try {
        reportServer(JSON.parse((event as MessageEvent).data) as SseProgressPayload);
      } catch {
        /* ignore malformed */
      }
    });

    es.addEventListener('complete', (event) => {
      es.close();
      try {
        const data = JSON.parse((event as MessageEvent).data) as SseCompletePayload;
        onProgress?.(100);
        resolve(data);
      } catch {
        reject(new MediaApiError('Invalid completion event from server'));
      }
    });

    es.addEventListener('error', (event) => {
      es.close();
      if (event instanceof MessageEvent && event.data) {
        try {
          const data = JSON.parse(event.data) as SseErrorPayload;
          reject(new MediaApiError(data.message, undefined, data.code));
          return;
        } catch {
          /* fall through */
        }
      }
      reject(new MediaApiError('Conversion failed or connection lost'));
    });

    es.onerror = () => {
      /* EventSource fires error on close too; only reject if not already resolved */
    };
  });
}

export async function convertImage(
  file: File,
  fromFormat: ImageFormat,
  toFormat: ImageFormat,
  onProgress?: (percent: number) => void,
): Promise<ConvertApiResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fromFormat', fromFormat);
  formData.append('toFormat', toFormat);

  const uploadCap = 35;

  const { status, body, contentType } = await uploadWithProgress(
    `${MEDIA_API_PREFIX}/convert`,
    formData,
    (uploadPct) => {
      const overall = Math.round((uploadPct / 100) * uploadCap);
      onProgress?.(overall);
    },
  );

  if (status < 200 || status >= 300) {
    const message =
      contentType.includes('application/json') && body
        ? ((JSON.parse(body) as { message?: string }).message ?? 'Conversion failed')
        : body || `Conversion failed (${status})`;
    throw new MediaApiError(message, status);
  }

  if (contentType.includes('application/json')) {
    const data = JSON.parse(body) as ConvertApiResponse & JobAcceptedResponse & { url?: string };

    if (data.jobId && !data.downloadUrl && !data.url) {
      const result = await watchJobEvents(data.jobId, onProgress, uploadCap);
      return {
        downloadUrl: result.downloadUrl,
        fileName: result.fileName,
        mimeType: result.mimeType,
      };
    }

    onProgress?.(100);
    return {
      downloadUrl: data.downloadUrl || data.url,
      fileName: data.fileName,
      mimeType: data.mimeType,
      dataUrl: data.dataUrl,
    };
  }

  const blob = new Blob([body]);
  onProgress?.(100);

  return {
    blob,
    fileName: `converted.${toFormat === 'jpeg' ? 'jpg' : toFormat}`,
    mimeType: contentType,
  };
}

export async function checkMediaHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${MEDIA_API_PREFIX}/health`, {
      method: 'GET',
    });
    return response.ok;
  } catch {
    return false;
  }
}
